import { NgIf } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { ButtonComponent } from '@components/button/button.component';
import { HeaderComponent } from '@components/header/header.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, Location } from '@angular/common'
import { Router } from '@angular/router';
import { CosentinoSvgComponent } from '@components/cosentino-svg/cosentino-svg.component';
import { LinkComponent } from '@components/link/link.component';
import { ApiService, UserInfo } from 'app/services/api.service';
import { environment } from '../../../environments/environment';
import { DynamicBannerComponent } from '../mypoints/dynamic-banner/dynamic-banner-component';
import { ConfirmModalComponent } from '@components/confirm-modal/confirm-modal.component';
import { MenuService } from 'app/services/menu.service';
import { MenuItemType } from 'app/interfaces/api/MenuItemType';

@Component({
    selector: 'app-myrewards',
    templateUrl: './myrewards.component.html',
    styleUrl: './myrewards.component.scss',
    standalone: true,
    imports: [HeaderComponent, TranslateModule, NgIf, ButtonComponent, CosentinoSvgComponent, LinkComponent, CommonModule, DynamicBannerComponent, ConfirmModalComponent],
})
export class MyRewardsComponent {
    public currentSales = signal<number>(0)
    public nextLevelSales = signal<number>(0)
    public currentLevelSales = signal<number>(0)
    public currentStatus: string = ''
    public nextStatus: string = ''
    public availableActios: number = 0
    public actiosConditions: string = ''
    public accountOwnerEmail: string = ''
    public canExchangePoints: boolean = true
    public newPointsProviderModal: boolean = false
    public isLoyalty2026Active: boolean = environment.loyalty2026
    public currentExpositions: number = 0
    public progressText: string = ''
    public rewardsTcUrl: string | null = null

    getFormattedDate(): string {
        const currentDate = new Date()
        const currentLang = this.translateService.currentLang || 'es'

        // Mapear idiomas a locales
        const localeMap: { [key: string]: string } = {
            es: 'es-ES',
            en: 'en-US',
            'en-gb': 'en-GB',
            'en-ca': 'en-CA',
            'en-au': 'en-AU',
            fr: 'fr-FR',
            'fr-ca': 'fr-CA',
            de: 'de-DE',
            it: 'it-IT',
            pt: 'pt-BR',
            nl: 'nl-NL',
            pl: 'pl-PL',
            da: 'da-DK',
            no: 'no-NO',
            sv: 'sv-SE',
            fi: 'fi-FI',
            tr: 'tr-TR',
            iw: 'he-IL',
            ja: 'ja-JP',
        }

        const locale = localeMap[currentLang] || 'es-ES'

        return currentDate.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
    }

    constructor(private location: Location, private router: Router, private api: ApiService, private translateService: TranslateService, private menuService: MenuService) {}

    closeNewPointsModal() {
        this.newPointsProviderModal = false
        localStorage.setItem('newPointsProviderModalShown', 'true')
    }
    ngOnInit() {
        const alreadyShown = localStorage.getItem('newPointsProviderModalShown')

        if (!alreadyShown) {
            this.newPointsProviderModal = true
        }
        const userInfo = localStorage.getItem('weUserInfo')
        if (userInfo) {
            const { ytdSales, loyaltyLevel, country, availableActios, linksByCountry, authorities, accountOwnerEmail, completedDisplays, Completed_Displays__c, accountType } =
                JSON.parse(userInfo)
            this.currentSales.set(ytdSales)
            this.currentStatus = loyaltyLevel
            this.availableActios = availableActios
            this.accountOwnerEmail = accountOwnerEmail
            this.actiosConditions = linksByCountry.items.find((item: any) => item.code === 'actios-conditions').link
            this.canExchangePoints = Array.isArray(authorities) && authorities.some((authority: any) => authority === 'canExchangePoints')
            this.currentExpositions = (completedDisplays ?? Completed_Displays__c ?? 0) || 0
            setTimeout(() => this.updateProgressText(), 0)
            // authorities.some((authority: any) => authority === 'canExchangePoints')
            this.api.getNextLoyaltyLevelRequirements(this.currentStatus, country).subscribe({
                next: (nextLevel: any) => {
                    this.nextLevelSales.set(nextLevel.ytdSales ?? 0)
                    this.nextStatus = nextLevel.loyaltyStatus
                },
                error: (error: any) => console.log(error),
            })

            this.api.getCurrentLoyaltyLevelRequirements(this.currentStatus, country).subscribe({
                next: (currentLevel: any) => this.currentLevelSales.set(currentLevel.ytdSales ?? 0),
                error: (error: any) => console.log('Error al pedir los requisitos del nivel actual:', error),
            })

            this.api.getRewardsTc().subscribe({
                next: (res: any) => (this.rewardsTcUrl = res.url ?? null),
                error: () => (this.rewardsTcUrl = null),
            })
        }

        // Actualizar cuando cambie el idioma
        this.translateService.onLangChange.subscribe(() => {
            this.updateProgressText()
        })
    }

    updateProgressText(): void {
        this.progressText = this.getProgressText()
    }

    getProgressText(): string {
        if (!this.currentStatus) return ''

        const currentLevel = this.currentStatus.toLowerCase()
        const sales = this.currentSales() || 0
        const displays = this.currentExpositions || 0
        const eliteSales = 12,
            eliteDisplays = 2
        const premiumSales = 24,
            premiumDisplays = 3

        // Usuario Premium
        if (currentLevel === 'premium') {
            const hasReachedPremiumGoals = sales >= premiumSales && displays >= premiumDisplays

            if (hasReachedPremiumGoals) {
                // PREMIUM CON OBJETIVOS CUMPLIDOS
                return this.translateService.instant('benefits_kb.loyalty_2026.keep_registering_update_displays')
            } else {
                // PREMIUM SIN OBJETIVOS CUMPLIDOS
                const salesNeeded = Math.max(premiumSales - sales, 0)
                const displaysNeeded = Math.max(premiumDisplays - displays, 0)
                const salesWord =
                    salesNeeded === 1
                        ? this.translateService.instant('benefits_kb.loyalty_2026.sale_singular')
                        : this.translateService.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord =
                    displaysNeeded === 1
                        ? this.translateService.instant('benefits_kb.loyalty_2026.exposition_singular')
                        : this.translateService.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translateService.instant('benefits_kb.loyalty_2026.maintain_level_premium_dynamic', {
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord,
                })
            }
        }

        // Usuario Elite
        if (currentLevel === 'elite') {
            const hasReachedEliteGoals = sales >= eliteSales && displays >= eliteDisplays
            const hasReachedPremiumGoals = sales >= premiumSales && displays >= premiumDisplays

            if (hasReachedPremiumGoals) {
                // ELITE CON OBJETIVOS DE PREMIUM CUMPLIDOS
                return this.translateService.instant('benefits_kb.loyalty_2026.keep_registering_update_displays')
            } else if (!hasReachedEliteGoals) {
                // ELITE SIN OBJETIVOS DE ELITE CUMPLIDOS
                const salesNeeded = Math.max(eliteSales - sales, 0)
                const displaysNeeded = Math.max(eliteDisplays - displays, 0)
                const salesWord =
                    salesNeeded === 1
                        ? this.translateService.instant('benefits_kb.loyalty_2026.sale_singular')
                        : this.translateService.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord =
                    displaysNeeded === 1
                        ? this.translateService.instant('benefits_kb.loyalty_2026.exposition_singular')
                        : this.translateService.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translateService.instant('benefits_kb.loyalty_2026.maintain_level_requirements_elite_dynamic', {
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord,
                })
            } else {
                // ELITE CON OBJETIVOS DE ELITE CUMPLIDOS, PERO NO DE PREMIUM
                const salesNeeded = Math.max(premiumSales - sales, 0)
                const displaysNeeded = Math.max(premiumDisplays - displays, 0)
                const salesWord =
                    salesNeeded === 1
                        ? this.translateService.instant('benefits_kb.loyalty_2026.sale_singular')
                        : this.translateService.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord =
                    displaysNeeded === 1
                        ? this.translateService.instant('benefits_kb.loyalty_2026.exposition_singular')
                        : this.translateService.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translateService.instant('benefits_kb.loyalty_2026.level_up_premium_requirements_dynamic', {
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord,
                })
            }
        }

        // Usuario Advanced
        const salesNeeded = Math.max(eliteSales - sales, 0)
        const displaysNeeded = Math.max(eliteDisplays - displays, 0)
        const salesWord =
            salesNeeded === 1 ? this.translateService.instant('benefits_kb.loyalty_2026.sale_singular') : this.translateService.instant('benefits_kb.loyalty_2026.sale_plural')
        const displaysWord =
            displaysNeeded === 1
                ? this.translateService.instant('benefits_kb.loyalty_2026.exposition_singular')
                : this.translateService.instant('benefits_kb.loyalty_2026.exposition_plural')
        return this.translateService.instant('benefits_kb.loyalty_2026.level_up_one_display_dynamic', {
            sales: salesNeeded,
            salesWord: salesWord,
            displays: displaysNeeded,
            displaysWord: displaysWord,
        })
    }

    goBack() {
        this.location.back()
    }
    goToMyRewards() {
        this.router.navigate(['/my-rewards'])
    }
    goToMySales() {
        this.router.navigate(['/my-sales'])
    }

    goToRewardsProvider() {
        if (!this.canExchangePoints) return
        this.api.getRewardsProviderUrl().subscribe({
            next: (next) => {
                this.menuService.goTo({ type: MenuItemType.EXTERNAL_LINK, code: 'xoxoday', link: next.redirectUrl, requiresContract: 'retail-rewards' })
                // window.open(next.redirectUrl, '_blank')
            },
        })
    }

    goToRegisterSales() {
        this.router.navigate(['/my-sales'])
    }
    goToMyPoints() {
        this.router.navigate(['/my-points'])
    }
    knowYourBenefits() {
        window.open(this.actiosConditions, '_blank')
    }
    gotoBBenefits() {
        this.router.navigate(['/benefits'])
    }
    sendEmail() {
        const to = this.accountOwnerEmail
        const subject = 'Rewards Program'

        // Codificamos para que los espacios y acentos funcionen bien
        const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}}`

        window.location.href = mailto
    }
}
